import { useCallback, useRef, useState } from 'react';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { useJevContext } from '../../hooks/useJevContext';

export type Risk = 'read' | 'write' | 'sensitive' | 'external';
export interface Step { tool: string; ok: boolean; risk: Risk; ms: number; blocked?: string; error?: string; awaiting?: boolean }
export interface Confirmation { id: string; tool: string; summary: string; risk: Risk; expiresAt: string }
export interface Affected { type: string; id: string; label: string; href: string }
interface RunView {
  thread: { id: string; title: string };
  run: { id: string; status: string };
  message: string | null;
  toolCalls: Step[];
  confirmations: Confirmation[];
  affected: Affected[];
}
export interface Attached { name: string; text: string }
export interface Mention { type: string; id: string; label: string; hint?: string }
export interface ThreadRow { id: string; title: string; updatedAt: string }
/** Item da conversa na tela */
export type Item =
  | { kind: 'user'; text: string }
  | { kind: 'jev'; text: string; steps: Step[]; affected: Affected[]; runId?: string }
  | { kind: 'error'; text: string };

export const MAX_FILES = 3;
export const MAX_CHARS = 20_000;
export const ACCEPT = '.txt,.md,.csv,.json,.log,.tsv,.html,.xml,.yml,.yaml,text/plain,text/markdown,text/csv,application/json';

/**
 * Estado e ações de uma conversa com a Lumi. Usado pelo painel lateral e pela tela
 * Perguntar à IA (conversa na própria página, como o Notion AI).
 */
export function useJevChat() {
  const fetchApi = useAuthenticatedFetch();
  const page = useJevContext();
  const [threadId, setThreadId] = useState<string | null>(null);
  const [items, setItems] = useState<Item[]>([]);
  const [pending, setPending] = useState<Confirmation[]>([]);
  const [threads, setThreads] = useState<ThreadRow[]>([]);
  const [input, setInput] = useState('');
  const [files, setFiles] = useState<Attached[]>([]);
  const [marked, setMarked] = useState<Mention[]>([]);
  const [options, setOptions] = useState<Mention[]>([]);
  const [fileNote, setFileNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [deciding, setDeciding] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const mentionTimer = useRef<ReturnType<typeof setTimeout>>();
  const mentionSeq = useRef(0);

  const call = useCallback(async (url: string, body?: unknown) => {
    const r = await fetchApi(url, body === undefined ? {} : { method: 'POST', body: JSON.stringify(body) });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(data.message || `Não foi possível falar com a Lumi (${r.status})`);
    return data;
  }, [fetchApi]);

  const applyRun = (r: RunView) => {
    setThreadId(r.thread.id);
    if (r.message) setItems((x) => [...x, { kind: 'jev', text: r.message!, steps: r.toolCalls || [], affected: r.affected || [], runId: r.run.id }]);
    setPending(r.confirmations || []);
  };

  const send = async (text: string, fresh = false) => {
    const q = text.trim();
    if (!q || busy) return;
    const sentFiles = files;
    // só vão as marcações que continuam no texto
    const sentMarks = marked.filter((m) => q.includes(`@${m.label}`));
    setInput(''); setFiles([]); setMarked([]); setOptions([]); setFileNote('');
    if (fresh) { setThreadId(null); setItems([]); setPending([]); }
    setItems((x) => [...x, { kind: 'user', text: sentFiles.length ? `${q}\n\n📎 ${sentFiles.map((f) => f.name).join(', ')}` : q }]);
    setBusy(true);
    try {
      applyRun(await call('/api/jev/messages', { text: q, threadId: fresh ? null : threadId, page, attachments: sentFiles, mentions: sentMarks }));
    } catch (err) {
      setItems((x) => [...x, { kind: 'error', text: err instanceof Error ? err.message : 'Erro ao falar com a Lumi' }]);
    } finally {
      setBusy(false);
    }
  };

  const decide = async (c: Confirmation, approve: boolean) => {
    setDeciding(c.id);
    try {
      const r: RunView = await call(`/api/jev/confirmations/${c.id}`, { decision: approve ? 'approve' : 'reject', page });
      setItems((x) => [...x, { kind: 'user', text: approve ? `Confirmar: ${c.summary}` : `Cancelar: ${c.summary}` }]);
      applyRun(r);
    } catch (err) {
      setItems((x) => [...x, { kind: 'error', text: err instanceof Error ? err.message : 'Não foi possível registrar a decisão' }]);
      setPending((p) => p.filter((x) => x.id !== c.id));
    } finally {
      setDeciding(null);
    }
  };

  const addFiles = async (list: FileList | null) => {
    if (!list) return;
    const next = [...files];
    const skipped: string[] = [];
    for (const f of Array.from(list)) {
      if (next.length >= MAX_FILES) { skipped.push(`${f.name} (máx. ${MAX_FILES} arquivos)`); continue; }
      if (f.size > 500_000) { skipped.push(`${f.name} (grande demais)`); continue; }
      const text = await f.text();
      if (text.includes('\u0000')) { skipped.push(`${f.name} (não é texto)`); continue; }
      next.push({ name: f.name, text: text.slice(0, MAX_CHARS) });
      if (text.length > MAX_CHARS) skipped.push(`${f.name} cortado em ${MAX_CHARS / 1000} mil caracteres`);
    }
    setFiles(next);
    setFileNote(skipped.length ? `Não enviado ou cortado: ${skipped.join('; ')}. Aceito texto: txt, md, csv, json, log.` : '');
    if (fileRef.current) fileRef.current.value = '';
  };

  // "@" no fim do texto abre a busca de registros
  const onInput = (v: string) => {
    setInput(v);
    const m = v.match(/(?:^|\s)@([^@\s][^@\n]{0,40})?$/);
    clearTimeout(mentionTimer.current);
    const seq = ++mentionSeq.current;
    const q = (m?.[1] ?? '').trim();
    if (!m || q.length < 2) { setOptions([]); return; }
    // Espera a pessoa parar de digitar e ignora respostas de buscas antigas
    mentionTimer.current = setTimeout(() => {
      call(`/api/jev/mentions?q=${encodeURIComponent(q)}`)
        .then((d) => { if (seq === mentionSeq.current) setOptions(d.items || []); })
        .catch(() => { if (seq === mentionSeq.current) setOptions([]); });
    }, 250);
  };
  const pickMention = (o: Mention) => {
    setInput((v) => v.replace(/@[^@\n]*$/, `@${o.label} `));
    setMarked((m) => (m.some((x) => x.id === o.id) ? m : [...m, o]));
    setOptions([]);
    inputRef.current?.focus();
  };

  const newChat = () => {
    setThreadId(null); setItems([]); setPending([]); setFiles([]); setMarked([]);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const loadThreads = async () => {
    try { setThreads((await call('/api/jev/threads')).threads || []); } catch { setThreads([]); }
  };

  /** Abre uma conversa anterior; devolve false se não existir. */
  const loadThread = async (id: string) => {
    try {
      const d = await call(`/api/jev/threads/${id}`);
      const runs: Array<{ id: string; toolCalls: Step[]; affected: Affected[] }> = d.runs || [];
      setItems((d.messages || []).map((m: { role: string; content: string; runId: string | null }) => {
        if (m.role === 'user') return { kind: 'user', text: m.content } as Item;
        const run = runs.find((r) => r.id === m.runId);
        return { kind: 'jev', text: m.content, steps: run?.toolCalls || [], affected: run?.affected || [], runId: m.runId ?? undefined } as Item;
      }));
      setPending(d.confirmations || []);
      setThreadId(id);
    } catch (err) {
      setItems([{ kind: 'error', text: err instanceof Error ? err.message : 'Conversa não encontrada' }]);
    }
  };

  return {
    page, threadId, items, pending, threads, input, setInput, files, setFiles, marked, options, setOptions, fileNote,
    busy, deciding, fileRef, inputRef, send, decide, addFiles, onInput, pickMention, newChat, loadThreads, loadThread,
  };
}

export type JevChat = ReturnType<typeof useJevChat>;
